import Collection from "./collection";
import GameTree from "./gameTree";
import Property from "./property";

class Node {

  static PROP_IDENT_ORDER = [
    // Root Properties
    "FF", "GM", "SZ", "AP",
    "CA",
    "ST",
    // Game Info Properties
    "PB", "BR", "BT", "PW", "WR", "WT", "HA", "KM", "RE", "GN",
    "DT", "EV", "RO", "PC", "TM", "OT", "GC", "RU", "ON", "SO",
    "CP", "US", "AN",
    // Move Properties
    "KO", "B", "W", "MN",
    // Node name
    "N",
    // Setup Properties
    "AB", "AE", "AW", "PL",
    // Markup Properties
    "AR", "CR", "DD", "LB", "LN", "MA", "SL", "SQ", "TB", "TR",
    "TW",
    // Node Annotation Properties
    "DM", "GB", "GW", "HO", "UC", "V",
    // Move Annotation Properties
    "BM", "DO", "IT", "TE",
    // Timing Properties
    "BL", "OB", "WL", "OW",
    // Miscellaneous Properties
    "FG", "PM", "VW",
    // Comment
    "C"];

  static GAME_INFO_PROPERTIES = [
    "AN", "BR", "BT", "CP", "DT", "EV", "GC", "GN", "HA", "KM", "ON", "OT",
    "PB", "PC", "PW", "RE", "RO", "RU", "SO", "TM", "US", "WR", "WT"
  ];

  static ROOT_PROPERTIES = ["AP", "CA", "FF", "GM", "ST", "SZ"];

  constructor() {
    this.parent = undefined;
    this.properties = [];
  }

  /**
   * Returns the number of properties.
   */
  get length() {
    return this.properties.length;
  }

  /**
   * Creates a property and adds it to this node.
   * 
   * @param {String} ident 
   * @param  {...any} values 
   */
  add(ident, ...values) {
    if (ident instanceof Property) {
      throw new TypeError('ident must be a String');
    }
    if (!values) {
      console.warn("No values.");
    }
    this.addProperty(new Property(ident, Array.from(values)));
    return this;
  }

  /**
   * Adds a property.
   * 
   * @param {Property} property 
   */
  addProperty(property) {
    if (!(property instanceof Property)) {
      throw new TypeError('Not a Property');
    }
    this.properties.push(property);
    return this;
  }

  /**
   * Removes a property
   * 
   * @param {*} propIdent 
   * @returns this
   */
  remove(propIdent) {
    if (propIdent instanceof Property) {
      throw new TypeError('propIdent must be a String.');
    }
    const p = this.getProperty(propIdent);
    if (p) {
      this.removeProperty(p);
    }
  }

  /**
   * Removes a property
   * 
   * @param {Property} property
   * @returns this
   */
  removeProperty(property) {
    if (!(property instanceof Property)) {
      throw new TypeError('Not a Property');
    }
    this.properties = this.properties.filter(p => p !== property);
    return this;
  }

  contains(propIdent) {
    return this.properties.some(property => property.ident === propIdent);
  }

  containsMove() {
    return this.contains('B') || this.contains('W');
  }

  containsGameInfoProperty() {
    return this.properties.some(property => property.ident
      && Node.GAME_INFO_PROPERTIES.indexOf(property.ident) !== -1);
  }

  containsRootProperty() {
    return this.properties.some(property => property.ident
      && Node.ROOT_PROPERTIES.indexOf(property.ident) !== -1);
  }

  containsPassMove() {
    const b = this.getProperty('B');
    const w = this.getProperty('W');
    if (b) {
      return !b.value || b.value === 'tt';
    } else if (w) {
      return !w.value || w.value === 'tt';
    }
    return false;
  }

  isEmpty() {
    return this.properties.length === 0;
  }

  isRootNode() {
    return this.parent
      && this.parent.nodes.indexOf(this) === 0
      && this.parent.isRootGameTree();
  }

  isNextBlackTurn() {
    if (this.contains("PL") && this.getProperty("PL").values[0] === "B") {
      return true;
    } else if (this.contains("PL") && this.getProperty("PL").values[0] === "W") {
      return false;
    } else if (this.contains("B")) {
      return false;
    } else if (this.contains("W")) {
      return true;
    } else if (this.getNextNode() && this.getNextNode().contains("B")) {
      return true;
    } else if (this.getNextNode() && this.getNextNode().contains("W")) {
      return false;
    } else if (this.isRootNode()) {
      if (!this.contains("HA")) {
        return true;
      } 
      const ha = parseInt(this.getProperty("HA").values[0]);      
      return isNaN(ha) || ha < 2;
    }
    return true;
  }

  getNodesFromAncestor() {
    if (!(this.parent instanceof GameTree)) {
      return [this];
    }
    let nodes = [];
    for (const node of this.parent.nodes) {
      nodes.push(node);
      if (node === this) {
        break;
      }
    }
    let gt = this.parent.parent;
    while (gt instanceof GameTree) {
      nodes = gt.nodes.concat(nodes);
      gt = gt.parent;
    }
    return nodes;
  }

  getProperty(propIdent) {
    return this.properties.find(property => property.ident === propIdent);
  }

  compare(a, b) {
    if (!(a instanceof Property) || !(b instanceof Property)) {
      throw new TypeError('Not a Property');
    }
    let indexA = Node.PROP_IDENT_ORDER.indexOf(a.ident);
    let indexB = Node.PROP_IDENT_ORDER.indexOf(b.ident);
    if (indexA === -1) {
      indexA = 1000;
    }
    if (indexB === -1) {
      indexB = 1001;
    }
    if (indexA < indexB) {
      return -1;
    } else if (indexA > indexB) {
      return 1;
    }
    return 0;
  }

  getSiblingVariations() {
    if (!this.parent || !this.parent.parent || this !== this.parent.firstNode) {
      return [];
    }
    return this.parent.parent.children
      .filter(gameTree => gameTree.nodes.length > 0)
      .filter(gameTree => gameTree.nodes[0] !== this)
      .map(gameTree => gameTree.nodes[0]);
  }

  hasSiblingVariation() {
    return this.getSiblingVariations().length > 0;
  }

  getChildrenVariations() {
    if (!this.parent || !this.parent.children || this.parent.children.length === 0
      || this !== this.parent.lastNode) {
      return [];
    }
    return this.parent.children
      .filter(gameTree => gameTree.nodes.length > 0)
      .map(gameTree => gameTree.nodes[0]);
  }

  hasChildrenVariation() {
    return this.getChildrenVariations().length > 0;
  }

  /**
   * Returns true if this Node is the last move of the main tree.
   *
   * @return true if this Node is the last move of the main tree
   */
  isLastMove() {
    if (!this.containsMove()) {
      return false;
    }
    if (this.getNextSibling()) {
      return false;
    }
    if (!this.parent) {
      return false;
    }
    let gt = this.parent.rootGameTree;
    let lastNode = gt.lastNode;
    while (gt.children.length > 0) {
      gt = gt.children[0];
      lastNode = gt.lastNode;
    }
    return (this === lastNode);
  }

  /**
   * Returns the previous sibling or null if this is the first Node.
   *
   * @return the previous sibling
   */
  getPreviousSibling() {
    if (!this.parent) {
      return null;
    }
    const index = this.parent.nodes.indexOf(this);
    return index > 0 ? this.parent.nodes[index - 1] : null;
  }

  /**
   * Returns the next sibling or null if this is the last Node.
   *
   * @return the next sibling
   */
  getNextSibling() {
    if (!this.parent) {
      return null;
    }
    const index = this.parent.nodes.indexOf(this);
    const lastIndex = this.parent.nodes.length - 1;
    return index < lastIndex ? this.parent.nodes[index + 1] : null;
  }

  /**
   * Returns the previous Node or null if no previous Node. If this is
   * the first Node of sequence and there is a parent sequence, returns the
   * last Node of the parent sequence. If there is no parent sequence and
   * there is a previous Game, returns the first Node of the Game.
   *
   * @return the previous Node
   */
  getPreviousNode() {
    let node = this.getPreviousSibling();
    if (!node && this.parent) {
      const grandParent = this.parent.parent;
      if (grandParent instanceof GameTree) {
        node = grandParent.lastNode;
      } else if (grandParent instanceof Collection) {
        const index = grandParent.children.indexOf(this.parent);
        if (index > 0) {
          const gt = grandParent.children[index - 1];
          node = gt.firstNode;
        }
      }
    }
    return node;
  }

  /**
   * Returns the next Node or null if no next Node. If this Node is the last
   * Node of sequence and there is child GameTree(s), returns the first Node
   * of the first child GameTree.
   *
   * @return the next Node
   */
  getNextNode() {
    let node = this.getNextSibling();
    if (!node) {
      node = this.getFirstVariation();
    }
    return node;
  }

  /**
   * Returns the first variation or null if no variation.
   *
   * @return the first variation or null if no variation
   */
  getFirstVariation() {
    if (this.parent && this.parent.children.length > 0 && 
      this.parent.children[0].nodes.length > 0) {
        return this.parent.children[0].nodes[0];
    }
    return null;
  }

  toString() {
    this.properties = this.properties.sort(this.compare);
    return ";" + this.properties.reduce((str, property) => str + property, "");
  }
}

export default Node;